import { ApiError, call, type ImportSource } from "./api";

/** Design 26.11 F7: one run of the sales final import. */
export interface SalesRun {
  id: string;
  started_at: string;
  seconds: number;
  mode: "dry-run" | "apply";
  state: "ok" | "refused" | "failed";
  actor: string;
  locked: boolean;
  counts: Record<string, number>;
  refused: string;
}

export interface SalesIssue {
  tab: string;
  code: string;
  row: number | null;
  column: string;
  number: string | null;
  value: string | null;
}

/** One previous-collector name of the source, with its place in the competitor list. */
export interface Collector {
  key: string;
  name: string;
  rows: number;
  competitor_id: string | null;
  competitor: string | null;
  ignored: boolean;
}

export interface SalesRunDetail extends SalesRun {
  issues: SalesIssue[];
  collectors: Collector[];
}

export interface Competitor {
  id: string;
  name: string;
}

export interface SalesOverview {
  source: ImportSource | null;
  locked: { at: string; by: string } | null;
  runs: SalesRun[];
  newest: SalesRunDetail | null;
  competitors: Competitor[];
  can_edit: boolean;
}

export interface CollectorChoice {
  key: string;
  name: string;
  competitor_id?: string | null;
  new_name?: string | null;
  ignore?: boolean;
}

export const salesOverview = (token: string) => call<SalesOverview>("/import/sales", token);
export const salesRun = (token: string, id: string) => call<SalesRunDetail>(`/import/sales/runs/${encodeURIComponent(id)}`, token);
export const runSalesImport = (token: string, mode: SalesRun["mode"]) =>
  call<SalesRunDetail>("/import/sales/run", token, { method: "POST", body: JSON.stringify({ mode }) });
export const mapCollectors = (token: string, entries: CollectorChoice[]) =>
  call<{ counts: Record<string, number>; competitors: Competitor[] }>("/import/sales/collectors", token, {
    method: "PUT",
    body: JSON.stringify({ entries }),
  });
export const lockSalesImport = (token: string) => call<{ locked: { at: string; by: string } }>("/import/sales/lock", token, { method: "POST" });

export interface Contact {
  id: string;
  name: string;
  role: string;
  phone: string;
  email: string;
  source: "import" | "screen";
}

export interface Payee {
  id: string;
  name: string;
  mail_name: string;
  mail_address: string;
  bank_details_needed: boolean;
  source: "import" | "screen";
}

export type NoteKind = "sales" | "service";

export interface AccountNote {
  id: string;
  kind: NoteKind;
  noted_on: string | null;
  body: string;
  source: "import" | "screen";
  created_at: string;
  created_by: string;
}

/** The contacts, the payee and the notes of an account. An older API has no such route (404). */
export interface AccountSales {
  contacts: Contact[];
  payee: Payee | null;
  notes: AccountNote[];
  customer_since: string | null;
  import_locked: boolean;
  can_edit_contacts: boolean;
  can_edit_payee: boolean;
  can_add_sales_note: boolean;
  can_add_service_note: boolean;
}

export type ContactValues = Pick<Contact, "name" | "role" | "phone" | "email">;
export type PayeeValues = Pick<Payee, "name" | "mail_name" | "mail_address" | "bank_details_needed">;

const account = (id: string) => `/accounts/${encodeURIComponent(id)}`;
export const accountSales = (token: string, id: string) => call<AccountSales>(`${account(id)}/sales`, token);
export const addContact = (token: string, id: string, contactId: string, values: ContactValues) =>
  call<AccountSales>(`${account(id)}/contacts`, token, { method: "POST", body: JSON.stringify({ id: contactId, ...values }) });
export const changeContact = (token: string, id: string, contactId: string, values: ContactValues) =>
  call<AccountSales>(`${account(id)}/contacts/${encodeURIComponent(contactId)}`, token, { method: "PUT", body: JSON.stringify(values) });
export const removeContact = (token: string, id: string, contactId: string) =>
  call<AccountSales>(`${account(id)}/contacts/${encodeURIComponent(contactId)}`, token, { method: "DELETE" });
export const setPayee = (token: string, id: string, values: PayeeValues) =>
  call<AccountSales>(`${account(id)}/payee`, token, { method: "PUT", body: JSON.stringify(values) });
export const addNote = (token: string, id: string, note: { id: string; kind: NoteKind; noted_on: string | null; body: string }) =>
  call<AccountSales>(`${account(id)}/notes`, token, { method: "POST", body: JSON.stringify(note) });

/** True when the API is older than the route: the page then shows nothing for it. */
export const missingRoute = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 405);

export const errorText = (e: unknown) => (e instanceof ApiError ? e.message : "Cannot reach the server. Check your connection.");

export const COUNT_TEXT: Record<string, string> = {
  rows: "Rows in the source",
  accounts_matched: "Rows with an account",
  accounts_changed: "Accounts that change",
  contacts_new: "Contacts to write",
  contacts_removed: "Imported contacts to replace",
  payees_new: "New payees",
  payees_changed: "Changed payees",
  payees_bank_details_needed: "Payees flagged: bank details needed",
  payees_kept_screen: "Payees kept (changed on the screen)",
  contract_versions_new: "New contract versions",
  notes_new: "Notes to add",
  notes_removed: "Imported notes to remove",
  check_ins_new: "Check-ins to add",
  collectors: "Previous-collector names",
  collectors_unmapped: "Names with no competitor",
  changed_after_lock: "Accounts changed after the lock",
  bank_columns: "Bank columns (never read)",
  issues: "Issues",
};

export const ISSUE_TEXT: Record<string, string> = {
  no_account: "No account has this number",
  deleted_on_screen: "The account is deleted",
  collector_unmapped: "The previous collector has no competitor",
  missing_rebate: "Deal dates with no rebate: no contract version",
  missing_deal_dates: "A rebate with no deal dates: no contract version",
  deal_end_before_start: "The deal ends before it starts",
  term_not_allowed: "The deal dates do not give 12, 24 or 36 months",
  salesperson_not_member: "The salesperson is not a user",
  payee_name_long: "The payment name is longer than 45 characters: the import cuts it",
  payee_changed_kept: "The payee changed on the screen: the import keeps it",
  bad_email: "An email address is not valid",
  bad_rebate: "The rebate is not a number",
  bad_rollover: "The term rollover is not yes or no",
  bad_date: "A date is not valid",
  too_long: "The text is too long",
  next_check_in_only: "A next check-in with no last check-in",
  next_before_last: "The next check-in is not after the last one",
  check_in_in_future: "The last check-in is in the future",
  sheet_changed_after_lock: "The source changed after the lock",
  duplicate_number: "The number is in the source more than once",
  missing_tab: "The source has no sales tab",
  missing_column: "A required column is missing",
  no_header: "The source has no header row",
};

export const issueText = (code: string) => ISSUE_TEXT[code] ?? code.replaceAll("_", " ");

/** The counts of a run in a fixed order, with only the counts that the run has. */
export function countLines(counts: Record<string, number>): [string, string, number][] {
  const known = Object.keys(COUNT_TEXT).filter((key) => key in counts);
  return known.map((key) => [key, COUNT_TEXT[key], counts[key]]);
}

/** The issues by code, the most frequent code first. */
export function issueGroups(issues: SalesIssue[]): { code: string; count: number }[] {
  const found = new Map<string, number>();
  for (const issue of issues) found.set(issue.code, (found.get(issue.code) ?? 0) + 1);
  return [...found.entries()].map(([code, count]) => ({ code, count })).sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
}

export type CollectorPick = { kind: "competitor"; id: string } | { kind: "new"; name: string } | { kind: "ignore" } | { kind: "none" };

export function pickOf(collector: Collector): CollectorPick {
  if (collector.ignored) return { kind: "ignore" };
  if (collector.competitor_id) return { kind: "competitor", id: collector.competitor_id };
  return { kind: "none" };
}

/** The body of the map: one entry for each name whose pick changed. */
export function mapEntries(collectors: Collector[], picks: Record<string, CollectorPick>): CollectorChoice[] {
  const entries: CollectorChoice[] = [];
  for (const collector of collectors) {
    const pick = picks[collector.key];
    if (!pick || JSON.stringify(pick) === JSON.stringify(pickOf(collector))) continue;
    const base = { key: collector.key, name: collector.name };
    if (pick.kind === "competitor") entries.push({ ...base, competitor_id: pick.id });
    else if (pick.kind === "new" && pick.name.trim()) entries.push({ ...base, new_name: pick.name.trim() });
    else if (pick.kind === "ignore") entries.push({ ...base, ignore: true });
    else if (pick.kind === "none") entries.push(base);
  }
  return entries;
}

export const NOTE_KIND_TEXT: Record<NoteKind, string> = { sales: "Sales", service: "Service" };

/** The newest note first; a note with no date after the dated ones. */
export function sortedNotes(notes: AccountNote[]): AccountNote[] {
  return [...notes].sort((a, b) => {
    if (a.noted_on !== b.noted_on) {
      if (a.noted_on === null) return 1;
      if (b.noted_on === null) return -1;
      return b.noted_on.localeCompare(a.noted_on);
    }
    return b.created_at.localeCompare(a.created_at);
  });
}

export const contactLine = (contact: Contact) => [contact.role, contact.phone, contact.email].filter(Boolean).join(" · ");
